import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Plus,
  Search,
  Filter,
  CheckSquare,
  Clock,
  User,
  Calendar,
  AlertCircle,
  Target,
  Flag,
  Eye,
  Edit,
  MoreVertical,
} from 'lucide-react';

interface Task {
  _id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  progress: number;
  dueDate: string;
  estimatedHours: number;
  actualHours: number;
  project: {
    _id: string;
    title: string;
  };
  assignedTo: {
    _id: string;
    firstName: string;
    lastName: string;
  }[];
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const TasksList: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [error, setError] = useState('');

  const [projectFilter, setProjectFilter] = useState('all');
  const [projects, setProjects] = useState<{ _id: string; title: string }[]>(
    []
  );

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/tasks`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
      } else {
        throw new Error('Failed to fetch tasks');
      }
    } catch (error) {
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      } else {
        throw new Error('Failed to fetch projects');
      }
    } catch (err) {
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'todo':
          return 'text-gray-300 bg-gray-800';
        case 'in-progress':
          return 'text-blue-300 bg-blue-900';
        case 'review':
          return 'text-purple-300 bg-purple-900';
        case 'completed':
          return 'text-green-300 bg-green-900';
        case 'cancelled':
          return 'text-red-300 bg-red-900';
        default:
          return 'text-gray-300 bg-gray-800';
      }
    } else {
      switch (status) {
        case 'todo':
          return 'text-gray-600 bg-gray-100';
        case 'in-progress':
          return 'text-blue-600 bg-blue-100';
        case 'review':
          return 'text-purple-600 bg-purple-100';
        case 'completed':
          return 'text-green-600 bg-green-100';
        case 'cancelled':
          return 'text-red-600 bg-red-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    if (theme === 'dark') {
      switch (priority) {
        case 'urgent':
          return 'text-red-300 bg-red-900';
        case 'high':
          return 'text-orange-300 bg-orange-900';
        case 'medium':
          return 'text-yellow-300 bg-yellow-900';
        case 'low':
          return 'text-green-300 bg-green-900';
        default:
          return 'text-gray-300 bg-gray-800';
      }
    } else {
      switch (priority) {
        case 'urgent':
          return 'text-red-600 bg-red-100';
        case 'high':
          return 'text-orange-600 bg-orange-100';
        case 'medium':
          return 'text-yellow-600 bg-yellow-100';
        case 'low':
          return 'text-green-600 bg-green-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    }
  };

  const getTimeUntilDue = (dueDate: string) => {
    const days = Math.ceil(
      (new Date(dueDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

 const filteredTasks = tasks.filter(task => {
  const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        task.description.toLowerCase().includes(searchTerm.toLowerCase());
  const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
  const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
  const matchesProject = projectFilter === 'all' || task.project?._id === projectFilter;

  return matchesSearch && matchesStatus && matchesPriority && matchesProject;
});


  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className={`${
                theme === 'dark'
                  ? 'bg-gray-900 border-gray-800'
                  : 'bg-white border-gray-200'
              } p-6 rounded-lg shadow-sm border animate-pulse`}
            >
              <div
                className={`h-4 ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                } rounded w-3/4 mb-4`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                } rounded w-full mb-2`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                } rounded w-2/3`}
              ></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}
          >
            Tasks
          </h1>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mt-1`}
          >
            Manage and track your tasks
          </p>
        </div>
        <Link
          to="/dashboard/tasks/create"
          className={`flex items-center px-4 py-2 ${
            theme === 'dark'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white rounded-lg transition-colors duration-200`}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Task
        </Link>
      </div>

      {error && (
        <div
          className={`${
            theme === 'dark'
              ? 'bg-red-900 border-red-800'
              : 'bg-red-50 border-red-200'
          } border rounded-lg p-4`}
        >
          <div className="flex items-center">
            <AlertCircle
              className={`w-5 h-5 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-500'
              } mr-2`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-red-300' : 'text-red-700'
              } text-sm`}
            >
              {error}
            </span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-900 border-gray-800'
            : 'bg-white border-gray-200'
        } p-6 rounded-lg shadow-sm border`}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                } w-4 h-4`}
              />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 w-full border ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-gray-200 focus:ring-blue-600'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
                } rounded-lg focus:ring-2 focus:border-transparent`}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-gray-200 focus:ring-blue-600'
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              } rounded-lg focus:ring-2 focus:border-transparent`}
            >
              <option value="all">All Status</option>
              <option value="todo">To Do</option>
              <option value="in-progress">In Progress</option>
              <option value="review">Review</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-gray-200 focus:ring-blue-600'
                  : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              } rounded-lg focus:ring-2 focus:border-transparent`}
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className={`px-3 py-2 border ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-900'
              } rounded-lg focus:ring-2 focus:border-transparent`}
            >
              <option value="all">All Projects</option>
              {projects.map((proj) => (
                <option key={proj._id} value={proj._id}>
                  {proj.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-12">
          <CheckSquare
            className={`w-16 h-16 ${
              theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
            } mx-auto mb-4`}
          />
          <h3
            className={`text-lg font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            } mb-2`}
          >
            No tasks found
          </h3>
          <p
            className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            } mb-6`}
          >
            {tasks.length === 0
              ? 'Tasks will appear here when they are assigned to you.'
              : 'Try adjusting your search or filter criteria.'}
          </p>
          <Link
            to="/dashboard/tasks/create"
            className={`inline-flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-lg transition-colors duration-200`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTasks.map((task) => (
            <div
              key={task._id}
              className={`${
                theme === 'dark'
                  ? 'bg-gray-900 border-gray-800 hover:bg-gray-800'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              } rounded-lg shadow-sm border hover:shadow-md transition-all duration-200`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <Link
                        to={`/dashboard/tasks/${task._id}`}
                        className={`text-lg font-semibold ${
                          theme === 'dark'
                            ? 'text-gray-100 hover:text-blue-400'
                            : 'text-gray-900 hover:text-blue-600'
                        }`}
                      >
                        {task.title}
                      </Link>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                          task.status
                        )}`}
                      >
                        {task.status.replace('-', ' ')}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getPriorityColor(
                          task.priority
                        )}`}
                      >
                        {task.priority}
                      </span>
                    </div>
                    <p
                      className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      } mb-3`}
                    >
                      {task.description}
                    </p>

                    <div
                      className={`flex items-center space-x-6 text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      <div className="flex items-center">
                        <Target className="w-4 h-4 mr-1" />
                        Project: {task?.project?.title}
                      </div>
                      {task.dueDate && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {getTimeUntilDue(task.dueDate)}
                        </div>
                      )}
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {task.actualHours}h / {task.estimatedHours}h
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/dashboard/tasks/${task._id}`}
                      className={`p-2 ${
                        theme === 'dark'
                          ? 'text-gray-500 hover:text-blue-400 hover:bg-gray-800'
                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      } rounded-lg transition-colors duration-200`}
                      title="View task"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <button
                      className={`p-2 ${
                        theme === 'dark'
                          ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                      } rounded-lg transition-colors duration-200`}
                      title="More options"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className={`${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      Progress
                    </span>
                    <span
                      className={`font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                      }`}
                    >
                      {task.progress}%
                    </span>
                  </div>
                  <div
                    className={`w-full ${
                      theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                    } rounded-full h-2`}
                  >
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${task.progress}%` }}
                    />
                  </div>
                </div>

                <div
                  className={`mt-4 pt-4 border-t ${
                    theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className={`flex items-center space-x-4 text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    >
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        Assigned to:{' '}
                        {task.assignedTo.length > 0
                          ? task.assignedTo
                              .map(
                                (user) => `${user.firstName} ${user.lastName}`
                              )
                              .join(', ')
                          : 'Unassigned'}
                      </div>
                      <div>
                        Created by: {task?.createdBy?.firstName}{' '}
                        {task?.createdBy?.lastName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TasksList;
